import { XIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { cn } from '@/lib/utils.js';

import { HintActionFooter } from './HintActionFooter.js';
import { HINTS } from './hint-config.js';
import { HintContent } from './HintContent.js';
import { isNodeSeen, markNodeSeen, skipAllHints } from './hint-store.js';

export interface HintCardProps {
  id: string;
  /** Fired when the card is closed via Got it, Skip all tips, or the header X. */
  onDismissed?: () => void;
}

/**
 * Destination hint for breadcrumb nodes. Latches visibility from `isNodeSeen` once at mount,
 * then marks the node seen and ignores the store afterward — otherwise mark-on-mount would
 * unmount the card before the user can read it.
 */
export function HintCard({ id, onDismissed }: HintCardProps): ReactNode {
  const hint = HINTS[id];
  const [visible] = useState(() => (hint ? !isNodeSeen(id) : false));
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (hint && visible) markNodeSeen(id);
  }, [hint, id, visible]);

  if (!hint || !visible || dismissed) return null;

  /** Closes this card and notifies the host (e.g. directory overlay). */
  function dismiss(): void {
    setDismissed(true);
    onDismissed?.();
  }

  /** Ends the whole tour and dismisses this card. */
  function handleSkipTour(): void {
    skipAllHints(Object.keys(HINTS));
    dismiss();
  }

  const wideVideo = hint.videoAspect === 'wide';

  return (
    <Card
      className={cn('relative', wideVideo ? 'w-full max-w-none' : 'max-w-md')}
      role="article"
      tabIndex={0}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{hint.title}</CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Close hint"
            onClick={dismiss}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <HintContent hint={hint} />
        <HintActionFooter onGotIt={dismiss} onSkipTour={handleSkipTour} />
      </CardContent>
    </Card>
  );
}
